import React, { useState, useEffect, useRef } from "react";
import { C } from "../constants";
import IpdButton from "../../ipd/utils/IpdButton";

// In-page PDF preview for the billing documents (bill + deposit receipt).
// Both printing flows hit an API before generating the file, so by the time the
// PDF exists the click's user-activation is gone and window.open() is blocked —
// which used to make the file download silently instead. Rendering the blob in
// an iframe avoids the popup blocker entirely, and Print / Download / New tab
// then fire from a real click inside this modal.
export default function PdfPreviewModal({ doc, filename, title = "Print Preview", subtitle, onClose, onLetterheadToggle }) {
  const [url, setUrl] = useState("");
  // When a regenerator is supplied, the preview owns the "active" doc so the
  // letterhead toggle can swap it in place; otherwise it just mirrors the prop.
  const [activeDoc, setActiveDoc] = useState(doc);
  const [withLetterhead, setWithLetterhead] = useState(true);
  const iframeRef = useRef(null);

  useEffect(() => { setActiveDoc(doc); setWithLetterhead(true); }, [doc]);

  useEffect(() => {
    if (!activeDoc) { setUrl(""); return undefined; }
    const objectUrl = URL.createObjectURL(activeDoc.output("blob"));
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [activeDoc]);

  const toggleLetterhead = (checked) => {
    setWithLetterhead(checked);
    if (onLetterheadToggle) setActiveDoc(onLetterheadToggle(checked));
  };

  // Print the embedded viewer. The blob URL is same-origin so this works in
  // Chrome/Edge; if a browser refuses, fall back to opening the file in a tab
  // and let the user print from there.
  const handlePrint = () => {
    try {
      const frame = iframeRef.current;
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      window.open(url, "_blank");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={onClose}>
      <div
        style={{ background: "#fff", borderRadius: "16px", width: "900px", maxWidth: "94vw", height: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 64px rgba(13,35,63,0.28)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}` }}>
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: "800", color: C.text }}>{title}</h3>
          {subtitle && <p style={{ margin: "4px 0 0", fontSize: "12px", color: C.textSub }}>{subtitle}</p>}
        </div>

        <div style={{ flex: 1, background: "#525659", minHeight: 0 }}>
          {url && (
            <iframe
              ref={iframeRef}
              src={`${url}#zoom=page-fit`}
              title={title}
              style={{ display: "block", width: "100%", height: "100%", border: "none" }}
            />
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "16px 24px", borderTop: `1px solid ${C.border}` }}>
          {onLetterheadToggle && (
            <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "600", color: C.textSub, cursor: "pointer", whiteSpace: "nowrap", marginRight: "4px" }}>
              <input type="checkbox" checked={withLetterhead} onChange={(e) => toggleLetterhead(e.target.checked)} />
              Letterhead
            </label>
          )}
          <IpdButton variant="outlined" onClick={onClose} sx={{ flex: 1 }}>Close</IpdButton>
          <IpdButton variant="outlined" onClick={() => window.open(url, "_blank")} sx={{ flex: 1 }}>Open in New Tab</IpdButton>
          <IpdButton variant="outlined" onClick={() => activeDoc.save(filename)} sx={{ flex: 1 }}>Download</IpdButton>
          <IpdButton onClick={handlePrint} sx={{ flex: 1 }}>Print</IpdButton>
        </div>
      </div>
    </div>
  );
}
